import { computed } from '@angular/core';
import { signalStoreFeature, type, withComputed } from '@ngrx/signals';
import { EntityProps, EntityState } from '@ngrx/signals/entities';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { DateTime } from 'luxon';

import { RéférentielLivrableState } from './référentiel-livrable.state';
import { RéférentielLivrable } from '../../administration/gestion-référentiels/models/référentiel-livrable.model';

type LivrablePrincipalType = (typeof LivrablePrincipal)[keyof typeof LivrablePrincipal];

function estLivrablePrincipal(value?: string): value is LivrablePrincipalType {
  return !!value ? Object.values<string>(LivrablePrincipal).includes(value) : false;
}

export function avecSélecteursRéférentielLivrables<_>() {
  return signalStoreFeature(
    {
      state: type<RéférentielLivrableState & EntityState<RéférentielLivrable>>(),
      props: type<EntityProps<RéférentielLivrable>>(),
    },
    // Filtres
    withComputed(({ entities, filtres }) => ({
      getFilteredLivrables: computed(() => {
        let r = [...entities()];
        const filtresActifs = filtres();
        if (filtresActifs) {
          if (filtresActifs.cartographies?.length) {
            r = r.filter((x) => filtresActifs.cartographies?.includes(x.cartographie.slug));
          }
          if (filtresActifs.services?.length) {
            r = r.filter((x) => filtresActifs.services?.includes(x.referentiel.referentielType));
          }
          if (filtresActifs.institutsMetiers?.length) {
            r = r.filter((x) => filtresActifs.institutsMetiers?.includes(x.referentiel.equipeResponsable));
          }
          if (filtresActifs.livrables?.length) {
            r = r.filter((x) => {
              const nomLivrablePrincipal = x.nomLivrableParent ?? x.nomLivrable;
              if (estLivrablePrincipal(nomLivrablePrincipal)) {
                return filtresActifs.livrables?.includes(nomLivrablePrincipal);
              }
              return false;
            });
          }
          if (filtresActifs.statuts?.length) {
            r = r.filter((x) => filtresActifs.statuts?.includes(x.etat));
          }
          if (filtresActifs.echeance) {
            let predicate: (x: RéférentielLivrable) => boolean;
            switch (filtresActifs.echeance) {
              case 'ONE_WEEK':
                predicate = (x) =>
                  x.dateEcheance ? DateTime.fromJSDate(new Date(x.dateEcheance)).diffNow('week').weeks < 1 : false;
                break;
              case 'ONE_MONTH':
                predicate = (x) =>
                  x.dateEcheance ? DateTime.fromJSDate(new Date(x.dateEcheance)).diffNow('month').months < 1 : false;
                break;
              case 'THREE_MONTHS':
                predicate = (x) =>
                  x.dateEcheance ? DateTime.fromJSDate(new Date(x.dateEcheance)).diffNow('month').months < 3 : false;
                break;
            }
            r = r.filter(predicate);
          }
        }
        return r;
      }),
    })),
    // Tri
    withComputed(({ getFilteredLivrables, sorts }) => ({
      sortedEntities: computed((): RéférentielLivrable[] => {
        const getValue = (obj: any, path: string) => path.split('.').reduce((acc, key) => acc?.[key], obj);

        const s = sorts?.();

        if (!s) return getFilteredLivrables();

        return [...getFilteredLivrables()].sort((a, b) => {
          for (const sort of s) {
            const aValue = getValue(a, sort.nom);
            const bValue = getValue(b, sort.nom);

            const result = compareValues(aValue, bValue);
            if (result !== 0) {
              return sort.direction === '+' ? result : -result;
            }
          }
          return 0;
        });
      }),
    })),
    // Pagination
    withComputed(({ paginator, sortedEntities }) => ({
      getPaginated: computed(() =>
        sortedEntities().slice(
          (paginator.pageNb() - 1) * paginator.pageSize(),
          paginator.pageNb() * paginator.pageSize(),
        ),
      ),
      totalPageNb: computed(() => Math.ceil(sortedEntities().length / paginator.pageSize()) || 1),
      firstItemNumber: computed(() => {
        if (sortedEntities().length === 0) {
          return 0;
        }
        return (paginator.pageNb() - 1) * paginator.pageSize() + 1;
      }),
      lastItemNumber: computed(() => {
        if (paginator.pageNb() === Math.ceil(sortedEntities().length / paginator.pageSize())) {
          return sortedEntities().length;
        }
        return paginator.pageNb() * paginator.pageSize();
      }),
      count: computed(() => sortedEntities().length),
    })),
  );
}

function compareValues(a: any, b: any): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b, 'fr', { sensitivity: 'base' });
  }

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  return String(a).localeCompare(String(b), 'fr', { sensitivity: 'base' });
}
